import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { Form } from '@/components/form/Form';
import { Loading } from '@/components/loading/Loading';
import MarginField from '@/components/margin/MarginField';
import { Modal } from '@/components/modal/Modal';
import { ModalProps } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import { useFilterPack, useUpdateFilterPack } from '@/services/api/hooks/catalog';
import { useCatalogueService } from '@/services/catalogue/useCatalogueService';
import { zodResolver } from '@hookform/resolvers/zod';

import { FC, Suspense } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

const ManageGlobalMarginModalFields = z.object({
  value: z.string()
});

type ManageGlobalMarginModalFields = z.infer<typeof ManageGlobalMarginModalFields>;

export const ManageGlobalMarginModal: FC<ModalProps> = props => {
  const ManageGlobalMarginForm = () => {
    const filterPackId = useCatalogueService(state => state.filterPackId);
    if (!filterPackId) throw new Error('Missing filter pack id');
    const [filterPack] = useFilterPack(filterPackId);
    const updateFilterPack = useUpdateFilterPack();

    const methods = useForm<ManageGlobalMarginModalFields>({
      resolver: zodResolver(ManageGlobalMarginModalFields),
      defaultValues: { value: String(filterPack.globalMarginModifier) }
    });

    const onSubmit = async (data: ManageGlobalMarginModalFields) => {
      await updateFilterPack.mutateAsync({
        ...filterPack,
        marginFilterIds: filterPack.marginFilters.map(filter => filter.id),
        productFilterIds: filterPack.productFilters.map(filter => filter.id),
        globalMarginModifier: Number(data.value)
      });
      toast.success('Successfully updated global margin');
      props.close();
    };

    return (
      <Form methods={methods} onSubmit={onSubmit} className="flex w-full flex-col gap-4 py-2">
        <MarginField id="value" />
        <Button
          loading={updateFilterPack.isLoading}
          className="w-fit place-self-end"
          variant={ButtonVariant.Primary}
          type="submit"
        >
          Update
        </Button>
      </Form>
    );
  };

  return (
    <Modal className="md:max-w-2xl" {...props}>
      <Card className="w-full p-8">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-semibold">Global margin</h1>
        </Modal.Controls>
        <p className="mt-0.5 leading-tight">
          Global margin is the default margin applied to all products. This margin can be overridden by other settings
          like criteria or product filters.
        </p>

        <Suspense fallback={<Loading />}>
          <ManageGlobalMarginForm />
        </Suspense>
      </Card>
    </Modal>
  );
};
